'use client';

import { useEffect } from 'react';
import { useAuthStore } from '@/lib/store/authStore';

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Restaurador de sesión.
 *
 * Evita el "bucle" entre /dashboard y /login que aparece cuando el navegador
 * conserva la **cookie** `token` pero el `localStorage` (store) quedó vacío
 * o desincronizado (p. ej. por limpieza de datos del sitio).
 *
 * - Si hay cookie y el store está vacío: intenta recuperar el usuario con
 *   `/api/auth/me`. Si funciona, restaura la sesión.
 * - Si la cookie ya no es válida: la borra para que el login normal funcione.
 */
export function SessionRestorer() {
  useEffect(() => {
    let cancelled = false;
    const cookieToken = getCookie('token');
    const store = useAuthStore.getState();

    // Ya hay sesión en el store o no hay cookie: no hay nada que hacer.
    if (!cookieToken || store.token) return;

    fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${cookieToken}` },
      cache: 'no-store',
    })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) throw new Error('sesión inválida');
        const data = await res.json();
        useAuthStore.getState().setToken(cookieToken);
        useAuthStore.getState().setUser({
          id: data.id,
          email: data.email,
          name: data.name,
          role: data.role,
          avatar: data.avatar ?? null,
          emailVerified: Boolean(data.emailVerifiedAt),
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Sesión inválida: `logout()` borra store y cookie, cortando el bucle.
        useAuthStore.getState().logout();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}